import React, { useState, useCallback, useMemo, useRef, useEffect ,useContext  } from "react";
import "../../assets/css/donordashboard/HelpSupport.css";
import MasterLayout from "../../components/layout/MasterLayout";
import AppContext from "../../context/AppContext";

const CATEGORIES = [
  "Account & Profile",
  "Donation Issues",
  "Payouts",
  "NGO Coordination",
  "General Inquiry",
  "Technical Bug",
];

const FAQ_DATA = [
  {
    category: "Account & Profile",
    items: [
      { q: "How do I update my account information?", a: 'Go to the Profile module from the sidebar. Click "Edit Profile" and update your name, address, CNIC, or contact details.' },
      { q: "I forgot my password. How do I reset it?", a: 'On the login screen, click "Forgot Password?" and enter your registered email. You will receive a reset link within 5 minutes.' },
    ],
  },
  {
    category: "Donations & Pickups",
    items: [
      { q: "What items are accepted as food donations?", a: "We accept non-perishable dry goods, sealed packaged food, and fresh vegetables/fruit. Items must be clearly labelled with expiry dates." },
      { q: "Can I cancel a scheduled donation?", a: "Yes, cancellations are allowed up to 4 hours before the scheduled pickup. Go to Donations → My Donations, find the entry, and click Cancel." },
    ],
  },
  {
    category: "Payouts & History",
    items: [
      { q: "Where can I see my donation history?", a: "The Payouts module shows a full log of all completed donations, including date, item type, quantity, and the receiving NGO." },
      { q: 'Why is my payout status showing "Pending"?', a: 'A payout moves to "Pending" when the NGO has collected but not yet confirmed receipt. This usually resolves within 24 hours.' },
    ],
  },
];

const STATUS_MAP = {
  pending: { label: "Pending", cls: "hs-badge--pending" },
  open:    { label: "Open",    cls: "hs-badge--open"    },
  closed:  { label: "Closed",  cls: "hs-badge--closed"  },
};

const Icon = {
  Search: () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>),
  X: () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>),
  Plus: () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>),
  Ticket: () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5v2m0 4v2m0 4v2M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2z" /></svg>),
  Headset: () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" /></svg>),
  AlertCircle: () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4m0 4h.01" /></svg>),
  CheckCircle: () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></svg>),
  Eye: () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>),
  Inbox: () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>),
  Spinner: () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="hs-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>),
  Send: () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>),
};

function highlightText(text, query) {
  if (!query.trim()) return text;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return parts.map((p, i) => p.toLowerCase() === query.toLowerCase() ? <mark key={i} className="hs-highlight">{p}</mark> : p);
}

function formatDate(d) {
  return new Date(d).toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" });
}

function formatTime(d) {
  if (!d) return "";
  return new Date(d).toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" });
}

function Badge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.pending;
  return <span className={`hs-badge ${s.cls}`}>{s.label}</span>;
}

function Toast({ msg, type = "success", onDone }) {
  const [exiting, setExiting] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setExiting(true), 3200);
    const t2 = setTimeout(onDone, 3700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);
  return (
    <div className={`hs-toast hs-toast--${type} d-flex align-items-center gap-2${exiting ? " hs-toast-exit" : ""}`}>
      <Icon.CheckCircle /> {msg}
    </div>
  );
}

function FaqItem({ q, a, searchQuery, openId, setOpenId, id }) {
  const isOpen = openId === id;
  return (
    <div className={`hs-faq-item mb-2${isOpen ? " hs-open" : ""}`}>
      <button className="hs-faq-q w-100 d-flex align-items-center justify-content-between gap-2" onClick={() => setOpenId(isOpen ? null : id)}>
        <span>{highlightText(q, searchQuery)}</span>
        <span className="hs-faq-q-icon flex-shrink-0"><Icon.Plus /></span>
      </button>
      <div className="hs-faq-a">
        <div className="hs-faq-a-inner">{highlightText(a, searchQuery)}</div>
      </div>
    </div>
  );
}

/* ─── DONOR CHAT BOX ──────────────────────────── */
function DonorChatBox({ ticket, onMessagesUpdate, baseUrl }) {
  const [messages, setMessages]       = useState([]);
  const [newMsg, setNewMsg]           = useState('');
  const [sending, setSending]         = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    setLoadingMsgs(true);
    try {
      const res  = await fetch(`${baseUrl}/donor/helpandsupport/messages/${ticket.id}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      if (data.success) setMessages(data.messages);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoadingMsgs(false);
    }
  };

  const handleSend = async () => {
    if (!newMsg.trim() || ticket.status === 'closed') return;
    setSending(true);
    try {
      const res  = await fetch(`${baseUrl}/donor/helpandsupport/message`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_id: ticket.id, message: newMsg.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setMessages(prev => [...prev, data.message]);
        setNewMsg('');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isClosed = ticket.status === 'closed';

  return (
    <div className="hs-chat-wrap">
      <div className="hs-chat-header">
        <span>💬 Conversation with NGO</span>
        <span className="hs-chat-count">{messages.length} messages</span>
      </div>

      <div className="hs-chat-messages">
        {loadingMsgs ? (
          <div className="hs-chat-loading"><Icon.Spinner /> Loading…</div>
        ) : messages.length === 0 ? (
          <div className="hs-chat-empty">No messages yet. The NGO will reply here.</div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.id}
              className={`hs-chat-bubble ${msg.sender_type === 'donor' ? 'hs-chat-bubble--donor' : 'hs-chat-bubble--ngo'}`}
            >
              <div className="hs-chat-bubble-label">
                {msg.sender_type === 'donor' ? '👤 You' : '🏢 NGO'}
              </div>
              <div className="hs-chat-bubble-text">{msg.message}</div>
              <div className="hs-chat-bubble-time">{formatTime(msg.created_at)}</div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Closed ticket pe donor message nahi kar sakta */}
      {isClosed ? (
        <div className="hs-chat-closed-notice">
          <Icon.CheckCircle /> This ticket is closed. You cannot send new messages.
        </div>
      ) : (
        <div className="hs-chat-input-wrap">
          <textarea
            className="hs-chat-input"
            placeholder="Type your message…"
            value={newMsg}
            onChange={e => setNewMsg(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
          />
          <button
            className="hs-chat-send-btn"
            onClick={handleSend}
            disabled={sending || !newMsg.trim()}
          >
            {sending ? <Icon.Spinner /> : <Icon.Send />}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── SUBMIT TICKET MODAL ─────────────────────── */
function TicketModal({ donorId, onClose, onSubmit, baseUrl }) {
  const [form, setForm]       = useState({ subject: "", category: "", description: "" });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: "" })); setApiError(""); };

  const validate = () => {
    const e = {};
    if (!form.subject.trim())     e.subject     = "Subject is required";
    if (!form.category)           e.category    = "Please select a category";
    if (!form.description.trim()) e.description = "Description is required";
    else if (form.description.trim().length < 10) e.description = "Please provide more detail (min 10 chars)";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${baseUrl}/donor/helpandsupport/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ donor_id: donorId, ...form }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Something went wrong");
      onSubmit(data.ticket);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const overlayRef = useRef();
  return (
    <div className="hs-modal-overlay" ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="hs-modal" role="dialog" aria-modal="true">
        <div className="hs-modal-header d-flex align-items-start justify-content-between gap-3">
          <div>
            <h2 className="hs-modal-title mb-1">Submit Support Ticket</h2>
            <p className="hs-modal-sub mb-0">Our team typically responds within 24 hours</p>
          </div>
          <button className="hs-modal-close" onClick={onClose}><Icon.X /></button>
        </div>
        <div className="hs-modal-body">
          {apiError && <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3"><Icon.AlertCircle /> {apiError}</div>}
          <div className="mb-3">
            <label className="hs-label form-label">Subject <span className="text-danger">*</span></label>
            <input className={`hs-input form-control${errors.subject ? " is-invalid" : ""}`} placeholder="Brief description of your issue" value={form.subject} onChange={e => set("subject", e.target.value)} maxLength={120} />
            {errors.subject && <div className="invalid-feedback">{errors.subject}</div>}
          </div>
          <div className="mb-3">
            <label className="hs-label form-label">Category <span className="text-danger">*</span></label>
            <select className={`hs-select form-select${errors.category ? " is-invalid" : ""}`} value={form.category} onChange={e => set("category", e.target.value)}>
              <option value="">Select category…</option>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
            {errors.category && <div className="invalid-feedback">{errors.category}</div>}
          </div>
          <div className="mb-2">
            <label className="hs-label form-label">Description <span className="text-danger">*</span></label>
            <textarea className={`hs-textarea form-control${errors.description ? " is-invalid" : ""}`} placeholder="Describe your issue in detail…" value={form.description} onChange={e => set("description", e.target.value)} maxLength={1000} />
            {errors.description && <div className="invalid-feedback">{errors.description}</div>}
            <div className="form-text text-end">{form.description.length} / 1000</div>
          </div>
        </div>
        <div className="hs-modal-footer d-flex align-items-center justify-content-end gap-2">
          <button className="hs-btn-ghost btn" onClick={onClose} disabled={loading}>Cancel</button>
          <button className={`hss-btn-primary btn d-inline-flex align-items-center gap-2${loading ? " hs-submit-loading" : ""}`} onClick={handleSubmit} disabled={loading}>
            {loading ? <><Icon.Spinner /> Submitting…</> : <><Icon.Ticket /> Submit Ticket</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── TICKET DETAIL MODAL (Donor) ─────────────── */
function TicketDetailModal({ ticket, onClose, baseUrl }) {
  const overlayRef = useRef();
  return (
    <div className="hs-modal-overlay" ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="hs-modal hs-modal--wide" role="dialog" aria-modal="true">
        <div className="hs-modal-header d-flex align-items-start justify-content-between gap-3">
          <div>
            <p className="hs-detail-label mb-1">Ticket Details</p>
            <h2 className="hs-modal-title mb-0">{ticket.subject}</h2>
          </div>
          <button className="hs-modal-close" onClick={onClose}><Icon.X /></button>
        </div>
        <div className="hs-modal-body">
          <div className="row g-3 mb-3">
            <div className="col-4">
              <div className="hs-detail-label">Ticket ID</div>
              <div className="hs-detail-value font-monospace">#{ticket.id}</div>
            </div>
            <div className="col-4">
              <div className="hs-detail-label">Status</div>
              <div className="hs-detail-value"><Badge status={ticket.status} /></div>
            </div>
            <div className="col-4">
              <div className="hs-detail-label">Category</div>
              <div className="hs-detail-value">{ticket.category}</div>
            </div>
          </div>
          <div className="row g-3 mb-3">
            <div className="col-6">
              <div className="hs-detail-label">Date Submitted</div>
              <div className="hs-detail-value">{formatDate(ticket.created_at)}</div>
            </div>
            {ticket.closed_at && (
              <div className="col-6">
                <div className="hs-detail-label">Date Closed</div>
                <div className="hs-detail-value">{formatDate(ticket.closed_at)}</div>
              </div>
            )}
          </div>
          <div className="hs-detail-label mb-2">Your Original Message</div>
          <div className="hs-detail-desc">{ticket.description}</div>

          {ticket.status === "closed" && (
            <div className="hs-closed-notice mt-3 d-flex align-items-center gap-2">
              <Icon.CheckCircle /> This ticket has been resolved and closed by the NGO.
            </div>
          )}
        </div>

        {/* ✅ Chat Box donor side */}
        <DonorChatBox ticket={ticket} baseUrl={baseUrl} />

        <div className="hs-modal-footer d-flex align-items-center justify-content-end">
          <button className="hs-btn-ghost btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN COMPONENT ──────────────────────────── */
const HelpSupport = () => {
  const { baseUrl } = useContext(AppContext);
  const donorId = localStorage.getItem("donor_id") || 1;

  const [search, setSearch]             = useState("");
  const [faqCat, setFaqCat]             = useState("All");
  const [openFaq, setOpenFaq]           = useState(null);
  const [tickets, setTickets]           = useState([]);
  const [ticketFilter, setTicketFilter] = useState("all");
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [viewTicket, setViewTicket]     = useState(null);
  const [toast, setToast]               = useState(null);
  const [loading, setLoading]           = useState(false);

  useEffect(() => { fetchTickets(); }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${baseUrl}/donor/helpandsupport/viewAll?donor_id=${donorId}`, {
        headers: { Accept: "application/json" },
      });
      const data = await res.json();
      if (data.success) setTickets(data.tickets);
    } catch (err) {
      console.error("Failed to fetch tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  const FAQ_CATS = ["All", ...FAQ_DATA.map(f => f.category)];
  const filteredFaqs = useMemo(() => {
    const q = search.toLowerCase().trim();
    return FAQ_DATA.map(group => ({
      ...group,
      items: group.items.filter(item =>
        (faqCat === "All" || group.category === faqCat) &&
        (!q || item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q))
      ),
    })).filter(g => g.items.length > 0);
  }, [search, faqCat]);

  const filteredTickets = useMemo(() => {
    if (ticketFilter === "all") return tickets;
    return tickets.filter(t => t.status === ticketFilter);
  }, [tickets, ticketFilter]);

  const handleTicketSubmit = useCallback((ticket) => {
    setTickets(prev => [ticket, ...prev]);
    setShowNewTicket(false);
    setToast({ msg: `Ticket #${ticket.id} submitted successfully!`, type: "success" });
  }, []);

  // View ticket — latest data fetch karo with messages
  const handleViewTicket = async (ticket) => {
    try {
      const res  = await fetch(`${baseUrl}/donor/helpandsupport/view/${ticket.id}`, {
        headers: { Accept: "application/json" },
      });
      const data = await res.json();
      if (data.success) setViewTicket(data.ticket);
      else setViewTicket(ticket);
    } catch {
      setViewTicket(ticket);
    }
  };

  return (
    <MasterLayout>
      <div className="hs-root">

        <div className="hs-hero">
          <div className="hs-hero-inner">
            <div className="hs-hero-badge"><Icon.Headset /> Help &amp; Support Center</div>
            <h1>How can we <em>help you</em> today?</h1>
            <p className="hs-hero-sub">Search our FAQs or raise a support ticket.</p>
            <div className="hs-search-wrap">
              <span className="hs-search-icon"><Icon.Search /></span>
              <input className="hs-search-input" placeholder="Search FAQs…" value={search} onChange={e => { setSearch(e.target.value); setFaqCat("All"); }} />
              {search && <button className="hs-search-clear" onClick={() => setSearch("")}><Icon.X /></button>}
            </div>
          </div>
        </div>

        <div className="hs-content">
          <div className="hs-faq-wrap mb-5">
            <p className="hs-section-title">Frequently Asked Questions</p>
            <div className="hs-faq-cats d-flex flex-wrap gap-2 mb-3">
              {FAQ_CATS.map(cat => (
                <button key={cat} className={`hs-cat-btn btn btn-sm${faqCat === cat ? " active" : ""}`} onClick={() => setFaqCat(cat)}>{cat}</button>
              ))}
            </div>
            {filteredFaqs.length === 0 ? (
              <p className="text-muted small">No FAQs match your search.</p>
            ) : (
              filteredFaqs.map(group => (
                <div key={group.category} className="mb-4">
                  <p className="hs-faq-group-title">{group.category}</p>
                  {group.items.map((item, i) => (
                    <FaqItem key={i} id={`${group.category}-${i}`} q={item.q} a={item.a} searchQuery={search} openId={openFaq} setOpenId={setOpenFaq} />
                  ))}
                </div>
              ))
            )}
          </div>

          <div className="hs-tickets-wrap mb-5">
            <p className="hs-section-title">My Support Tickets</p>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <div className="d-flex align-items-center gap-2">
                <select className="hs-tickets-filter form-select form-select-sm" value={ticketFilter} onChange={e => setTicketFilter(e.target.value)} style={{ width: "auto" }}>
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
                <span className="small text-muted">{filteredTickets.length} ticket{filteredTickets.length !== 1 ? "s" : ""}</span>
              </div>
              <button className="hss-btn-primary btn d-inline-flex align-items-center gap-2" onClick={() => setShowNewTicket(true)}>
                <Icon.Plus /> New Ticket
              </button>
            </div>

            <div className="hs-ticket-table-wrap table-responsive">
              {loading ? (
                <div className="text-center py-5 text-muted"><Icon.Spinner /> Loading tickets…</div>
              ) : filteredTickets.length === 0 ? (
                <div className="hs-ticket-empty text-center py-5">
                  <div style={{ width: 36, height: 36, margin: "0 auto 10px", opacity: 0.3 }}><Icon.Inbox /></div>
                  <p className="mb-0">No tickets found.</p>
                </div>
              ) : (
                <table className="hs-ticket-table table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>#ID</th>
                      <th>Subject</th>
                      <th>Category</th>
                      <th className="d-none d-md-table-cell">Date</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map(t => (
                      <tr key={t.id}>
                        <td><span className="hs-ticket-id">#{t.id}</span></td>
                        <td><span className="hs-ticket-subject fw-semibold">{t.subject}</span></td>
                        <td className="text-muted small">{t.category}</td>
                        <td className="text-muted small d-none d-md-table-cell">{formatDate(t.created_at)}</td>
                        <td><Badge status={t.status} /></td>
                        <td>
                          <button className="hs-ticket-action-btn btn btn-sm d-inline-flex align-items-center gap-1" onClick={() => handleViewTicket(t)}>
                            <Icon.Eye /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {showNewTicket && <TicketModal donorId={donorId} onClose={() => setShowNewTicket(false)} onSubmit={handleTicketSubmit} baseUrl={baseUrl} />}
        {viewTicket && <TicketDetailModal ticket={viewTicket} onClose={() => setViewTicket(null)} baseUrl={baseUrl} />}
        {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
      </div>
    </MasterLayout>
  );
};

export default HelpSupport;